import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowRightIcon,
  BellRingingIcon,
  ChatCircleTextIcon,
  ClockCountdownIcon,
  CheckIcon,
  PencilSimpleIcon,
  WarningOctagonIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import {
  approveDraft,
  closeEscalation,
  editDraft,
  fetchAttention,
  rejectDraft,
  type AttentionItem,
} from '@/features/attention/api';
import {
  executePendingAction,
  fetchPendingActions,
  rejectPendingAction,
  type PendingActionView,
} from '@/features/autonomy/api';
import { TOOL_LABELS_RU } from '@smena/core/labels';
import {
  Button,
  Card,
  EmptyState,
  ListGroupHeader,
  PageHeader,
  Segmented,
  SkeletonRows,
  cn,
  fmtRelativeTime,
} from '@smena/ui';

type Filter = 'all' | 'escalation' | 'action' | 'draft';

export function AttentionPage() {
  const q = useQuery({
    queryKey: ['attention'],
    queryFn: fetchAttention,
    refetchInterval: 5_000,
  });
  // Подтверждения живут здесь же: и то и другое - «Smena не может дальше без вас».
  // Отдельный раздел меню для второй очереди только заставлял гадать, куда смотреть.
  const actionsQ = useQuery({
    queryKey: ['pending-actions', 'pending'],
    queryFn: () => fetchPendingActions('pending'),
    refetchInterval: 5_000,
  });
  const [filter, setFilter] = useState<Filter>('all');

  const items = q.data?.items ?? [];
  const escalations = items.filter((i) => i.type === 'escalation');
  const drafts = items.filter((i) => i.type === 'draft');
  const actions = actionsQ.data?.items ?? [];
  const total = items.length + actions.length;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={<BellRingingIcon weight="bold" />}
        title="Внимание"
        description="Всё, что Smena не решила сама и ждёт вас."
        actions={
          total > 0 ? (
            <Segmented
              aria-label="Что показывать"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: `Всё ${total}` },
                { value: 'escalation', label: `Ответы ${escalations.length}` },
                { value: 'action', label: `Действия ${actions.length}` },
                { value: 'draft', label: `Черновики ${drafts.length}` },
              ]}
            />
          ) : null
        }
      />

      {q.isLoading ? (
        <Card>
          <SkeletonRows rows={3} />
        </Card>
      ) : total === 0 ? (
        <Card>
          <EmptyState
            tone="good"
            icon={<CheckIcon weight="bold" />}
            title="Smena справляется сама"
            description="Здесь появится всё, что она не решит без вас: спорные вопросы, деньги, действия с подтверждением."
          />
        </Card>
      ) : (
        // Один список с липкими заголовками групп вместо россыпи карточек: так видно
        // объём работы целиком, а не по одному экрану за раз.
        <Card className="overflow-hidden">
          {filter !== 'draft' && escalations.length > 0 ? (
            <>
              <ListGroupHeader tone="urgent" title="Ждут вашего ответа" count={escalations.length} />
              {escalations.map((item) => (
                <AttentionRow key={`${item.type}:${item.id}`} item={item} />
              ))}
            </>
          ) : null}
          {filter !== 'escalation' && filter !== 'draft' && actions.length > 0 ? (
            <>
              <ListGroupHeader
                title="Ждут вашей кнопки"
                count={actions.length}
                action={
                  <Link to="/pending-actions" className="text-accent-fg hover:underline">
                    вся история
                  </Link>
                }
              />
              {actions.map((item) => (
                <PendingRow key={item.id} item={item} />
              ))}
            </>
          ) : null}
          {filter !== 'escalation' && filter !== 'action' && drafts.length > 0 ? (
            <>
              <ListGroupHeader title="Черновики на проверку" count={drafts.length} />
              {drafts.map((item) => (
                <AttentionRow key={`${item.type}:${item.id}`} item={item} />
              ))}
            </>
          ) : null}
        </Card>
      )}
    </div>
  );
}

function AttentionRow({ item }: { item: AttentionItem }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isEsc = item.type === 'escalation';
  const openDialog = () => {
    if (item.clientKey) navigate(`/dialogs/${encodeURIComponent(item.clientKey)}`);
  };

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['attention'] });
    void qc.invalidateQueries({ queryKey: ['clients'] });
  };

  const closeEsc = useMutation({
    mutationFn: () => closeEscalation(item.id),
    onSuccess: () => {
      invalidate();
      toast.success('Эскалация закрыта');
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const approve = useMutation({
    mutationFn: () => approveDraft(item.id),
    onSuccess: () => {
      invalidate();
      toast.success('Ответ отправлен гостю');
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const reject = useMutation({
    mutationFn: () => rejectDraft(item.id),
    onSuccess: () => {
      invalidate();
      toast.success('Черновик отклонён');
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(item.body);
  const edit = useMutation({
    mutationFn: () => editDraft(item.id, editText.trim()),
    onSuccess: () => {
      invalidate();
      setEditing(false);
      toast.success('Правка отправлена гостю');
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const busy = closeEsc.isPending || approve.isPending || reject.isPending || edit.isPending;

  return (
    <div className="flex gap-3 border-b border-line px-4 py-3 last:border-b-0">
      <span
        className={cn(
          'mt-0.5 grid size-7 shrink-0 place-items-center rounded-full',
          isEsc ? 'bg-destructive/10 text-destructive' : 'bg-amber/10 text-amber',
        )}
      >
        {isEsc ? (
          <WarningOctagonIcon weight="fill" className="size-4" />
        ) : (
          <ChatCircleTextIcon weight="fill" className="size-4" />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 truncate font-semibold">{item.title}</span>
          <span className="ml-auto shrink-0 text-xs text-muted">
            {fmtRelativeTime(item.createdAt)}
          </span>
        </div>

        {!editing ? (
          <button
            type="button"
            onClick={openDialog}
            disabled={!item.clientKey}
            className={cn(
              'mt-1 block w-full text-left text-sm leading-snug whitespace-pre-wrap text-muted',
              item.clientKey && 'cursor-pointer hover:text-text',
            )}
            title={item.clientKey ? 'Открыть диалог с гостем' : undefined}
          >
            {item.body}
          </button>
        ) : (
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={3}
            className="mt-1 w-full resize-y rounded-md border border-line-control bg-input px-3 py-2 text-sm"
            autoFocus
          />
        )}

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {isEsc ? (
            <>
              <Button size="sm" onClick={openDialog} disabled={!item.clientKey}>
                Ответить <ArrowRightIcon weight="bold" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => closeEsc.mutate()} disabled={busy}>
                <CheckIcon weight="bold" /> Уже решено
              </Button>
            </>
          ) : editing ? (
            <>
              <Button size="sm" onClick={() => edit.mutate()} disabled={busy || !editText.trim()}>
                <CheckIcon weight="bold" /> Отправить правку
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Отмена
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" onClick={() => approve.mutate()} disabled={busy}>
                <CheckIcon weight="bold" /> Отправить гостю
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                <PencilSimpleIcon weight="bold" /> Править
              </Button>
              <Button size="sm" variant="ghost" onClick={() => reject.mutate()} disabled={busy}>
                <XIcon weight="bold" /> Не отправлять
              </Button>
            </>
          )}
          {item.clientKey && !isEsc && !editing ? (
            <button
              type="button"
              onClick={openDialog}
              className="ml-1 text-xs font-semibold text-accent-fg hover:underline"
            >
              открыть диалог
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Строка подтверждения. Раньше это был отдельный раздел с JSON-простынёй и кодом
 * инструмента: владельцу такое читать нечем. Здесь - что Smena собирается сделать
 * словами, сколько осталось времени и две кнопки. Аргументы вызова спрятаны под
 * «подробности» - они нужны, когда что-то пошло не так, а не каждый раз.
 */
function PendingRow({ item }: { item: PendingActionView }) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [open, setOpen] = useState(false);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['pending-actions'] });
    void qc.invalidateQueries({ queryKey: ['attention'] });
  };

  const executeMut = useMutation({
    mutationFn: () => executePendingAction(item.id),
    onSuccess: () => {
      invalidate();
      toast.success('Готово, Smena выполнила');
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const rejectMut = useMutation({
    mutationFn: () => rejectPendingAction(item.id, reason.trim() || undefined),
    onSuccess: () => {
      invalidate();
      setRejecting(false);
      setReason('');
      toast.success('Отменили');
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const label =
    (TOOL_LABELS_RU as Record<string, { title: string; description: string }>)[item.toolName];
  const leftMs = new Date(item.expiresAt).getTime() - Date.now();
  const leftMin = Math.max(0, Math.round(leftMs / 60000));
  const busy = executeMut.isPending || rejectMut.isPending;

  return (
    <div className="flex gap-3 border-b border-line px-4 py-3 last:border-b-0">
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-amber/10 text-amber">
        <ClockCountdownIcon weight="fill" className="size-4" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 truncate font-semibold">{label?.title ?? item.toolName}</span>
          <span className="ml-auto shrink-0 text-xs text-muted">
            {leftMs > 0 ? `осталось ${leftMin} мин` : 'время вышло'}
          </span>
        </div>
        {label?.description ? (
          <div className="mt-0.5 text-sm leading-snug text-muted">{label.description}</div>
        ) : null}

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {rejecting ? (
            <>
              <input
                type="text"
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="почему не нужно (необязательно)"
                className="min-w-40 flex-1 rounded-md border border-line-control bg-input px-2 py-1 text-sm"
              />
              <Button size="sm" onClick={() => rejectMut.mutate()} disabled={busy}>
                <XIcon weight="bold" /> Отменить
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>
                Назад
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" onClick={() => executeMut.mutate()} disabled={busy}>
                <CheckIcon weight="bold" /> Разрешить
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRejecting(true)} disabled={busy}>
                <XIcon weight="bold" /> Не нужно
              </Button>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="ml-1 text-xs font-semibold text-accent-fg hover:underline"
              >
                {open ? 'скрыть подробности' : 'подробности'}
              </button>
            </>
          )}
        </div>

        {open ? (
          <pre className="mt-2 overflow-x-auto rounded-md bg-panel2 p-3 text-[11px] leading-snug whitespace-pre-wrap">
            {JSON.stringify(item.toolCall, null, 2)}
          </pre>
        ) : null}
      </div>
    </div>
  );
}
